/**
 * Whether the browser has promised to keep this device's data.
 *
 * Shown as a warning to anyone who can see settings, until the browser says yes.
 * The owner should learn about this while setting a till up - not on the day a
 * full disk takes the unsent sales with it.
 */
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useT } from '../i18n/index.js';
import { useSync } from '../sync.js';
import { useAuth } from '../state/auth.js';
import { useToast } from '../state/toast.js';
import { persistIsGrantable } from '../storage.js';
import { Button } from './ui.js';

const DISMISS_KEY = 'pos.storageNoticeDismissed';

/** What to say, and whether asking again could ever change the answer. */
function useStorageWords() {
  const t = useT();
  const { storagePersisted, retryPersist } = useSync();
  const toast = useToast();
  const grantable = persistIsGrantable();

  const ask = async () => {
    const r = await retryPersist();
    // Never leave the press looking like nothing happened.
    if (r === 'granted') toast.ok(t('net.storageNowKept'));
    else toast.warn(t('net.storageStillNo'));
  };

  if (storagePersisted === 'granted') {
    return { ok: true, askable: false, ask, long: t('net.storageGranted'), short: t('net.storageGranted') };
  }
  if (storagePersisted === 'unsupported') {
    return { ok: false, askable: false, ask, long: t('net.storageUnsupported'), short: t('net.storageUnsupportedShort') };
  }
  if (!grantable) {
    // iPhone and iPad: the answer is no and no button will change it.
    return { ok: false, askable: false, ask, long: t('net.storageIos'), short: t('net.storageIosShort') };
  }
  return { ok: false, askable: true, ask, long: t('net.storageDenied'), short: t('net.storageDeniedShort') };
}

/** Full card, for the Settings screen. Always shows the current answer. */
export function StorageCard() {
  const t = useT();
  const { storagePersisted } = useSync();
  const w = useStorageWords();
  const [busy, setBusy] = useState(false);
  if (storagePersisted === null) return null;

  return (
    <div className={`card ${w.ok ? '' : 'note-warn'}`} data-testid="storage-card" data-state={storagePersisted}>
      <h2>{t('net.storageTitle')}</h2>
      <p className="small">{w.long}</p>
      {w.askable ? (
        <div className="row">
          <Button size="sm" busy={busy} onClick={() => { setBusy(true); void w.ask().finally(() => setBusy(false)); }}>
            {t('net.storageRetry')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * One-line warning, for people who can act on it, on the screens where sales
 * wait to upload. Shown on every screen it was noise on the owner's laptop
 * reading a report, and three lines of it on a phone pushed the work away.
 */
const WHERE_SALES_WAIT = ['/checkout', '/exchange'];

export function StorageNotice() {
  const t = useT();
  const { pathname } = useLocation();
  const { storagePersisted } = useSync();
  const w = useStorageWords();
  const { can } = useAuth();
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(DISMISS_KEY) === '1');
  if (dismissed || storagePersisted === null || storagePersisted === 'granted' || !can('settings.view')) return null;
  if (!WHERE_SALES_WAIT.includes(pathname)) return null;

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };
  return (
    <div className="note-warn storage-notice" role="status" data-testid="storage-notice">
      <span className="small">{w.short}</span>
      <span className="spacer" />
      {w.askable ? (
        <Button size="sm" variant="ghost" onClick={() => void w.ask()}>{t('net.storageRetry')}</Button>
      ) : null}
      <Button size="sm" variant="ghost" onClick={dismiss} aria-label={t('common.close')}>✕</Button>
    </div>
  );
}
